import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { useLocation } from "react-router-dom";
import { NAV } from "../Navbar/navbarTheme";
import { sectionTitleSpacingSx } from "../sectionHeaderStyles";
import autismEarlySupportIconImg from "../Images/EventsImages/Autism Early Support.jpeg";
import eventSupportIconImg from "../Images/EventsImages/Event Support.jpeg";
import fmcgProductsIconImg from "../Images/EventsImages/FMCG Products.jpeg";
import sponsorshipFundingIconImg from "../Images/EventsImages/Sponsorship Funding.jpeg";
import communitySupportIconImg from "../Images/EventsImages/Community Support & Sponsorship.png";
import tradeShowImg from "../Images/EventsImages/Trade show.png";
import { EVENTS_API_URL } from "../utils/shopApi";

const communitySx = {
  navy: "#0b2a4a",
  badge: "#f4c430",
  panelCream: "#fdf8e8",
  gold: "#c9a227",
  accentBlue: "#1d6fd4",
  tagBg: "#f3f4f6",
  tagBorder: "#e5e7eb",
};

const eventsSx = {
  red: "#e31837",
  navy: NAV.navy,
  ink: NAV.ink,
  muted: NAV.inkMuted,
  line: NAV.line,
};

const sponsorshipTags = [
  { image: fmcgProductsIconImg, label: "FMCG Products" },
  { image: eventSupportIconImg, label: "Event Support" },
  { image: sponsorshipFundingIconImg, label: "Sponsorship Funding" },
];

const pageContentSx = {
  px: { xs: 2.5, sm: 3, md: 4, lg: 5 },
  py: { xs: 3.5, md: 5 },
  maxWidth: 1240,
  mx: "auto",
  width: "100%",
  boxSizing: "border-box",
};

const sectionBodySx = {
  color: NAV.ink,
  fontSize: { xs: "0.9375rem", md: "1.0625rem" },
  lineHeight: 1.7,
};

function EventsImage({ src, alt }) {
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        display: "block",
        width: "100%",
        maxWidth: 920,
        height: "auto",
        mx: "auto",
        borderRadius: { xs: 2.5, md: 3 },
        boxShadow: "0 10px 32px rgba(15, 40, 71, 0.1)",
      }}
    />
  );
}

const formatEventDate = (value) => {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const getEventImages = (event) =>
  [...(event?.images ?? [])]
    .sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))
    .map((image) => image?.url)
    .filter(Boolean);

function EventImageLightbox({ open, images, startIndex, title, onClose }) {
  const [activeIndex, setActiveIndex] = useState(startIndex);

  useEffect(() => {
    if (open) setActiveIndex(startIndex);
  }, [open, startIndex]);

  if (!images.length) return null;

  const goPrev = () =>
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  const goNext = () => setActiveIndex((prev) => (prev + 1) % images.length);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#0f172a",
          backgroundImage: "none",
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: { xs: 280, sm: 420 },
          bgcolor: "#0f172a",
        }}
      >
        <IconButton
          aria-label="Close gallery"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 2,
            color: "#fff",
            bgcolor: "rgba(15, 23, 42, 0.55)",
            "&:hover": { bgcolor: "rgba(15, 23, 42, 0.8)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        {images.length > 1 ? (
          <>
            <IconButton
              aria-label="Previous image"
              onClick={goPrev}
              sx={{
                position: "absolute",
                left: { xs: 4, sm: 12 },
                zIndex: 2,
                color: "#fff",
                bgcolor: "rgba(15, 23, 42, 0.55)",
                "&:hover": { bgcolor: "rgba(15, 23, 42, 0.8)" },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              aria-label="Next image"
              onClick={goNext}
              sx={{
                position: "absolute",
                right: { xs: 4, sm: 12 },
                zIndex: 2,
                color: "#fff",
                bgcolor: "rgba(15, 23, 42, 0.55)",
                "&:hover": { bgcolor: "rgba(15, 23, 42, 0.8)" },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        ) : null}

        <Box
          component="img"
          src={images[activeIndex]}
          alt={title ? `${title} — image ${activeIndex + 1}` : "Event image"}
          sx={{
            display: "block",
            maxWidth: "100%",
            maxHeight: { xs: "70vh", sm: "75vh" },
            objectFit: "contain",
            px: { xs: 5, sm: 8 },
          }}
        />
      </Box>

      <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff" }}>
        <Typography sx={{ fontWeight: 700, color: eventsSx.navy, mb: 0.5 }}>
          {title}
        </Typography>
        {images.length > 1 ? (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75 }}>
            {images.map((_, index) => (
              <Box
                key={`dot-${index}`}
                component="button"
                type="button"
                aria-label={`Show image ${index + 1}`}
                onClick={() => setActiveIndex(index)}
                sx={{
                  width: 8,
                  height: 8,
                  p: 0,
                  border: 0,
                  borderRadius: "50%",
                  cursor: "pointer",
                  bgcolor:
                    index === activeIndex
                      ? eventsSx.red
                      : "rgba(148, 163, 184, 0.55)",
                }}
              />
            ))}
          </Box>
        ) : null}
      </Box>
    </Dialog>
  );
}

function EventCard({ event, onImageClick }) {
  const images = getEventImages(event);
  const coverImage = images[0] ?? "";

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${eventsSx.line}`,
        bgcolor: "#fff",
        boxShadow: "0 8px 24px rgba(15, 40, 71, 0.06)",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => images.length && onImageClick(event, 0)}
        disabled={!coverImage}
        aria-label={
          coverImage
            ? `View photos for ${event.title}`
            : `No photos for ${event.title}`
        }
        sx={{
          display: "block",
          width: "100%",
          p: 0,
          border: 0,
          cursor: coverImage ? "pointer" : "default",
          bgcolor: "#f8fafc",
          "&:hover img": coverImage ? { transform: "scale(1.03)" } : undefined,
        }}
      >
        {coverImage ? (
          <Box
            component="img"
            src={coverImage}
            alt={event.title}
            sx={{
              display: "block",
              width: "100%",
              aspectRatio: "16 / 10",
              objectFit: "cover",
              transition: "transform 0.25s ease",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              aspectRatio: "16 / 10",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: eventsSx.muted,
              fontSize: 14,
            }}
          >
            No image
          </Box>
        )}
      </Box>

      <Box
        sx={{
          p: { xs: 1.5, sm: 1.75 },
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 1,
            color: eventsSx.muted,
          }}
        >
          <CalendarMonthOutlinedIcon
            sx={{ fontSize: 18, color: eventsSx.red }}
          />
          <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 500 }}>
            {formatEventDate(event.event_date)}
          </Typography>
        </Box>

        <Typography
          component="h3"
          sx={{
            color: eventsSx.navy,
            fontSize: { xs: "1rem", sm: "1.05rem" },
            fontWeight: 800,
            lineHeight: 1.3,
            mb: 0.75,
          }}
        >
          {event.title}
        </Typography>

        {event.address ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              mb: 1,
              color: eventsSx.muted,
            }}
          >
            <LocationOnOutlinedIcon
              sx={{ fontSize: 18, color: eventsSx.red, mt: 0.15 }}
            />
            <Typography sx={{ fontSize: { xs: 13, sm: 14 }, lineHeight: 1.45 }}>
              {event.address}
            </Typography>
          </Box>
        ) : null}

        <Typography
          sx={{
            color: eventsSx.ink,
            fontSize: { xs: 13, sm: 14 },
            lineHeight: 1.6,
            display: "-webkit-box",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {event.description}
        </Typography>
      </Box>
    </Box>
  );
}

function IndustryEventsSection() {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  const cardsPerPage = isLgUp ? 4 : isMdUp ? 3 : isSmUp ? 2 : 1;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [lightbox, setLightbox] = useState({
    open: false,
    images: [],
    startIndex: 0,
    title: "",
  });

  useEffect(() => {
    let isMounted = true;

    const loadEvents = async () => {
      try {
        setLoading(true);
        const response = await fetch(EVENTS_API_URL);
        if (!response.ok) throw new Error("Failed to load events");
        const payload = await response.json();
        const data = Array.isArray(payload?.data) ? payload.data : [];

        if (isMounted) {
          setEvents(
            data
              .filter((event) => event?.is_active !== false)
              .sort(
                (a, b) =>
                  (a?.sort_order ?? 0) - (b?.sort_order ?? 0) ||
                  String(a?.event_date ?? "").localeCompare(
                    String(b?.event_date ?? ""),
                  ),
              ),
          );
          setError("");
        }
      } catch {
        if (isMounted) {
          setEvents([]);
          setError("Unable to load events right now.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadEvents();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setPageIndex(0);
  }, [cardsPerPage, events.length]);

  const pageCount = Math.max(1, Math.ceil(events.length / cardsPerPage));
  const visibleEvents = events.slice(
    pageIndex * cardsPerPage,
    pageIndex * cardsPerPage + cardsPerPage,
  );

  const openLightbox = (event, startIndex = 0) => {
    const images = getEventImages(event);
    if (!images.length) return;
    setLightbox({
      open: true,
      images,
      startIndex,
      title: event.title ?? "",
    });
  };

  return (
    <Box
      component="section"
      aria-label="Industry events"
      sx={{ mb: { xs: 4, md: 6 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: { xs: 1.25, md: 1.5 },
        }}
      >
        <CalendarMonthOutlinedIcon sx={{ fontSize: 28, color: eventsSx.red }} />
        <Typography
          component="h2"
          sx={{
            color: eventsSx.navy,
            fontSize: { xs: "1.35rem", md: "1.65rem" },
            fontWeight: 800,
            lineHeight: 1.25,
          }}
        >
          Industry Events
        </Typography>
      </Box>

      <Typography
        sx={{
          ...sectionBodySx,
          mb: { xs: 3, md: 3.5 },
          maxWidth: 920,
        }}
      >
        We actively participate in business, FMCG, logistics, and export-related
        events to strengthen partnerships, explore new opportunities, and stay
        connected with industry developments.
      </Typography>

      {error ? (
        <Typography color="error" sx={{ mb: 2, fontWeight: 600 }}>
          {error}
        </Typography>
      ) : null}

      {loading ? (
        <Typography sx={{ color: eventsSx.muted, py: 3 }}>
          Loading events...
        </Typography>
      ) : null}

      {!loading && !error && events.length > 0 ? (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: { xs: 2, md: 2.5 },
              mb: 2.5,
            }}
          >
            {visibleEvents.map((event) => (
              <EventCard
                key={event.id ?? event.slug}
                event={event}
                onImageClick={openLightbox}
              />
            ))}
          </Box>

          {pageCount > 1 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
              }}
            >
              {Array.from({ length: pageCount }, (_, index) => (
                <Box
                  key={`page-${index}`}
                  component="button"
                  type="button"
                  aria-label={`Show events page ${index + 1}`}
                  onClick={() => setPageIndex(index)}
                  sx={{
                    width: 10,
                    height: 10,
                    p: 0,
                    border: 0,
                    borderRadius: "50%",
                    cursor: "pointer",
                    bgcolor:
                      index === pageIndex
                        ? eventsSx.red
                        : "rgba(148, 163, 184, 0.45)",
                    transition: "transform 0.2s ease",
                    transform: index === pageIndex ? "scale(1.1)" : "scale(1)",
                  }}
                />
              ))}
            </Box>
          ) : null}
        </>
      ) : null}

      {!loading && !error && events.length === 0 ? (
        <Typography sx={{ color: eventsSx.muted, py: 2 }}>
          No upcoming events to display.
        </Typography>
      ) : null}

      <EventImageLightbox
        open={lightbox.open}
        images={lightbox.images}
        startIndex={lightbox.startIndex}
        title={lightbox.title}
        onClose={() => setLightbox((prev) => ({ ...prev, open: false }))}
      />
      <Box sx={{ mb: { xs: 3, md: 5, lg: 6 }, mt: { xs: 3, md: 4, lg: 5 } }}>
        <EventsImage
          src={tradeShowImg}
          alt="Trade shows — FMCG and international trade exhibitions"
        />
      </Box>
    </Box>
  );
}

function CommunitySupportSection() {
  return (
    <Box
      component="section"
      aria-label="Community support and sponsorship"
      sx={{
        bgcolor: communitySx.navy,
        px: { xs: 2.5, sm: 3, md: 4, lg: 5 },
        py: { xs: 4, md: 5.5 },
      }}
    >
      <Box sx={{ maxWidth: 1240, mx: "auto", width: "100%" }}>
        <Box sx={{ textAlign: "center", mb: { xs: 3, md: 4 } }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2.5,
              py: 0.75,
              mb: { xs: 2, md: 2.5 },
              borderRadius: 999,
              bgcolor: communitySx.badge,
              color: NAV.ink,
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              fontWeight: 800,
              letterSpacing: "0.08em",
            }}
          >
            OUR COMMITMENT
          </Box>

          <Typography
            component="h2"
            sx={{
              color: "#fff",
              fontSize: {
                xs: "1.5rem",
                sm: "1.85rem",
                md: "2.25rem",
                lg: "2.5rem",
              },
              fontWeight: 800,
              lineHeight: 1.2,
              mb: { xs: 1.5, md: 2 },
            }}
          >
            Community Support &amp; Sponsorship
          </Typography>

          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.92)",
              fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.125rem" },
              lineHeight: 1.65,
              maxWidth: 760,
              mx: "auto",
            }}
          >
            We believe in supporting meaningful community initiatives through
            long-term partnerships and sponsorship.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            borderRadius: { xs: 2.5, md: 3 },
            overflow: "hidden",
            boxShadow: "0 18px 48px rgba(0, 0, 0, 0.22)",
          }}
        >
          <Box
            sx={{
              flex: { md: "0 0 34%" },
              bgcolor: communitySx.panelCream,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: { xs: 220, sm: 260, md: "100%" },
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={communitySupportIconImg}
              alt=""
              aria-hidden
              sx={{
                display: "block",
                width: "100%",
                height: "100%",
                minHeight: { xs: 220, sm: 260, md: 280 },
                objectFit: "cover",
                objectPosition: "center",
              }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              bgcolor: "#fff",
              display: "flex",
              flexDirection: "column",
              p: { xs: 2.5, sm: 3, md: 4 },
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.1, sm: 1.25 },
                mb: 0.75,
              }}
            >
              <Box
                component="img"
                src={autismEarlySupportIconImg}
                alt=""
                aria-hidden
                sx={{
                  width: { xs: 33, sm: 38, md: 41 },
                  height: { xs: 33, sm: 38, md: 41 },
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
              <Typography
                component="h3"
                sx={{
                  color: communitySx.navy,
                  fontSize: { xs: "1.25rem", sm: "1.45rem", md: "1.65rem" },
                  fontWeight: 800,
                  lineHeight: 1.25,
                }}
              >
                Autism Early Support
              </Typography>
            </Box>

            <Typography
              sx={{
                color: communitySx.accentBlue,
                fontSize: { xs: "0.95rem", sm: "1rem", md: "1.0625rem" },
                fontWeight: 700,
                lineHeight: 1.4,
                mb: { xs: 2, md: 2.5 },
              }}
            >
              Long-term sponsorship partner
            </Typography>

            <Divider sx={{ borderColor: NAV.line, mb: { xs: 2, md: 2.5 } }} />

            <Typography
              sx={{
                color: NAV.inkMuted,
                fontSize: { xs: "0.9375rem", md: "1.02rem" },
                lineHeight: 1.7,
                fontStyle: "italic",
                mb: { xs: 2.5, md: 3 },
                flex: 1,
              }}
            >
              We&apos;re proud to support Autism Early Support as a long-term
              partner for their community activities throughout the year.
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {sponsorshipTags.map(({ image, label }) => (
                <Box
                  key={label}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    bgcolor: communitySx.tagBg,
                    border: `1px solid ${communitySx.tagBorder}`,
                  }}
                >
                  <Box
                    component="img"
                    src={image}
                    alt=""
                    aria-hidden
                    sx={{
                      width: 20,
                      height: 20,
                      objectFit: "contain",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      color: NAV.ink,
                      fontSize: { xs: "0.78rem", sm: "0.8125rem" },
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const EventsAndCommunityPage = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Box component="main" sx={{ width: "100%" }}>
      <Box sx={pageContentSx}>
        <Box
          component="section"
          aria-label="Events and community introduction"
          sx={{
            mb: { xs: 4, md: 5 },
            pb: { xs: 3, md: 4 },
            borderBottom: `1px solid ${NAV.line}`,
            textAlign: "center",
          }}
        >
          <Typography
            component="h1"
            sx={{
              color: NAV.navy,
              fontSize: {
                xs: "1.65rem",
                sm: "1.9rem",
                md: "2.25rem",
                lg: "2.5rem",
              },
              fontWeight: 800,
              lineHeight: 1.2,
              ...sectionTitleSpacingSx,
              mb: { xs: 1.5, md: 2 },
            }}
          >
            Events &amp; Community
          </Typography>
          <Typography
            sx={{
              color: NAV.inkMuted,
              fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
              lineHeight: 1.65,
              maxWidth: 820,
              mx: "auto",
            }}
          >
            Supporting industry growth, business networking, and community
            initiatives through events, partnerships, and sponsorship.
          </Typography>
        </Box>

        <IndustryEventsSection />
      </Box>

      <CommunitySupportSection />
    </Box>
  );
};

export default EventsAndCommunityPage;
